import { useEffect, useState } from "react";

/**
 * Scroll-position flags without forcing layout during scroll.
 *
 * The old pattern (reading document.documentElement.scrollHeight inside an
 * unthrottled scroll handler) forces a synchronous relayout of the whole
 * page on every scroll event whenever layout is dirty — which it constantly
 * is on image-heavy pages — and makes the page checkerboard while scrolling.
 *
 * Here the document height is cached and refreshed by a ResizeObserver
 * (which also catches infinite-scroll appends and images loading in), and
 * the passive scroll handler is throttled to one window.scrollY read per
 * animation frame.
 */
export function useScrollEdges(edgeOffset = 1000) {
  const [flags, setFlags] = useState({
    atTop: true,
    nearTop: true,
    nearBottom: true,
    atBottom: true,
  });

  useEffect(() => {
    let maxScroll = 0;
    let rafId = null;

    const update = () => {
      rafId = null;
      const scrollTop = window.scrollY;
      const next = {
        atTop: scrollTop <= 0,
        nearTop: scrollTop <= edgeOffset,
        nearBottom: scrollTop >= maxScroll - edgeOffset,
        atBottom: scrollTop >= maxScroll - 1,
      };
      setFlags((prev) =>
        prev.atTop === next.atTop &&
        prev.nearTop === next.nearTop &&
        prev.nearBottom === next.nearBottom &&
        prev.atBottom === next.atBottom
          ? prev
          : next,
      );
    };

    const schedule = () => {
      if (rafId === null) rafId = requestAnimationFrame(update);
    };

    const measure = () => {
      maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      schedule();
    };

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(document.body);

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", measure);
    measure();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", measure);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [edgeOffset]);

  return flags;
}
